import {
  Input,
  Output,
  Component,
  OnDestroy,
  ViewChild,
  OnChanges,
  ElementRef,
  EventEmitter,
  AfterViewInit,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Subject } from 'rxjs';
import { NgStyle } from '@angular/common';

export interface AutocompleteSuggestion {
  label: string;
  type?: string;
  info?: string;
  apply?: string | Function;
  data?: any;
}

export interface DropdownPosition {
  top: number;
  left: number;
  maxHeight?: number;
}

@Component({
  selector: 'app-typeahead-dropdown',
  templateUrl: './typeahead-dropdown.component.html',
  styleUrls: ['./typeahead-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle],
})
export class TypeaheadDropdownComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @ViewChild('dropdownContainer', { static: true })
  dropdownContainer!: ElementRef<HTMLDivElement>;

  @Input() suggestions: AutocompleteSuggestion[] = [];
  @Input() position: DropdownPosition = { top: 0, left: 0 };
  @Input() isVisible: boolean = false;
  @Input() selectedIndex: number = -1;
  @Input() maxItems: number = 10;

  @Output() suggestionSelected = new EventEmitter<AutocompleteSuggestion>();
  @Output() suggestionHovered = new EventEmitter<number>();
  @Output() dropdownClosed = new EventEmitter<void>();

  private destroy$ = new Subject<void>();
  private boundClickOutside = this.handleClickOutside.bind(this);

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['suggestions'] ||
      changes['isVisible'] ||
      changes['position'] ||
      changes['selectedIndex']
    ) {
      this.cdr.markForCheck();
    }

    // * DEBUG: Log selectedIndex changes to verify keyboard navigation
    if (changes['selectedIndex'] && this.isVisible) {
      // console.log(
      //   '🎯 Dropdown selectedIndex changed to:',
      //   this.selectedIndex,
      //   'suggestion:',
      //   this.suggestions[this.selectedIndex]?.label || 'none',
      // );

      // * Auto-scroll selected item into view
      this.scrollSelectedItemIntoView();
    }
  }

  ngAfterViewInit(): void {
    this.setupKeyboardNavigation();
    this.setupClickOutside();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // * Clean up click outside listener
    document.removeEventListener('click', this.boundClickOutside);
  }

  onSuggestionClick(suggestion: AutocompleteSuggestion, index: number): void {
    this.suggestionSelected.emit(suggestion);
  }

  onSuggestionHover(index: number): void {
    // * Sync mouse hover with keyboard selection state
    this.suggestionHovered.emit(index);
  }

  getVisibleSuggestions(): AutocompleteSuggestion[] {
    return this.suggestions.slice(0, this.maxItems);
  }

  getDropdownStyle(): any {
    return {
      top: `${this.position.top}px`,
      left: `${this.position.left}px`,
      maxHeight: this.position.maxHeight
        ? `${this.position.maxHeight}px`
        : '21.428rem',
      width: '21.428rem',
      minWidth: '14.285rem',
      maxWidth: '21.428rem',
      overflow: 'hidden',
      boxSizing: 'border-box',
      // * to ensure dropdown interactions work properly
      pointerEvents: 'auto',
    };
  }

  // !Keyboard navigation will be handled by the parent component
  // ! This component focuses on rendering and mouse interactions
  private setupKeyboardNavigation(): void {}

  private setupClickOutside(): void {
    // * Set up click outside detection
    document.addEventListener('click', this.boundClickOutside);
  }

  private handleClickOutside(event: MouseEvent): void {
    if (!this.isVisible) return;

    const target = event.target as HTMLElement;
    if (!this.elementRef.nativeElement.contains(target)) {
      this.dropdownClosed.emit();
    }
  }

  trackBySuggestion(index: number, suggestion: AutocompleteSuggestion): string {
    return suggestion.label + index;
  }

  // * Scroll the selected item into view when navigating with keyboard
  private scrollSelectedItemIntoView(): void {
    if (this.selectedIndex < 0 || !this.isVisible) return;

    setTimeout(() => {
      try {
        const selectedElement =
          this.dropdownContainer.nativeElement.querySelector(
            `[data-index="${this.selectedIndex}"]`,
          ) as HTMLElement;

        if (selectedElement) {
          selectedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      } catch (error) {
        console.warn('Could not scroll selected item into view:', error);
      }
    }, 0);
  }
}
